import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { verifyAdmin, fetchLeads, deleteLead } from "@/lib/api";
import {
  Lock,
  RefreshCw,
  Trash2,
  Mail,
  Phone,
  Building2,
  MapPin,
  LogOut,
  ArrowRight,
} from "lucide-react";

const STORAGE_KEY = "af_admin_pw";

const AdminLeads = () => {
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  const tryAuth = async (candidate, fromStorage = false) => {
    setError("");
    setLoading(true);
    try {
      await verifyAdmin(candidate);
      sessionStorage.setItem(STORAGE_KEY, candidate);
      setAuthed(true);
      const data = await fetchLeads(candidate);
      setLeads(data);
    } catch (err) {
      if (fromStorage) sessionStorage.removeItem(STORAGE_KEY);
      else setError("Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      setPw(stored);
      tryAuth(stored, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await fetchLeads(pw);
      setLeads(data);
    } catch {
      setError("Failed to refresh leads.");
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPw("");
    setLeads([]);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await deleteLead(id, pw);
    setLeads((l) => l.filter((x) => x.id !== id));
  };

  const filtered =
    filter === "all" ? leads : leads.filter((l) => l.trade === filter);

  const trades = ["all", ...Array.from(new Set(leads.map((l) => l.trade)))];

  if (!authed) {
    return (
      <div className="min-h-screen bg-[var(--af-navy)] grid-bg flex items-center justify-center px-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            tryAuth(pw);
          }}
          className="surface p-8 max-w-md w-full"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2 mb-3">
            <Lock size={16} className="text-[var(--af-lime)]" />
            <div className="eyebrow">Admin Access</div>
          </div>
          <h1 className="font-display text-3xl mb-6">
            Anne Flinn <span className="text-[var(--af-lime)]">· Leads</span>
          </h1>
          <label className="block text-xs uppercase tracking-widest text-[var(--af-cream)]/65 mb-2">
            Password
          </label>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="af-input mb-3"
            autoFocus
            data-testid="admin-pw-input"
          />
          {error && (
            <div className="text-xs text-red-400 mb-3">{error}</div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="btn-lime w-full justify-center py-3 disabled:opacity-60"
            data-testid="admin-login-btn"
          >
            {loading ? "Checking..." : (<>Sign In <ArrowRight size={14} /></>)}
          </button>
          <Link
            to="/home-services"
            className="block text-xs text-center mt-5 text-[var(--af-cream)]/55 hover:text-[var(--af-lime)] underline-offset-4 hover:underline"
          >
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--af-navy)] text-[var(--af-cream)]">
      <header className="bg-[var(--af-navy)] border-b border-[var(--af-line)]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="eyebrow mb-1">Anne Flinn · Admin</div>
            <div className="font-display text-2xl">
              Leads <span className="text-[var(--af-lime)]">Dashboard</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={refresh}
              disabled={loading}
              className="btn-ghost"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={onLogout}
              className="btn-lime"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-center gap-2.5 mb-8">
          <div className="text-xs uppercase tracking-widest text-[var(--af-cream)]/65 mr-2">
            Filter:
          </div>
          {trades.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                filter === t
                  ? "bg-[var(--af-lime)] text-[var(--af-navy)] border-[var(--af-lime)] font-semibold"
                  : "border-[var(--af-line)] text-[var(--af-cream)]/75 hover:border-[var(--af-lime)]/50"
              }`}
              data-testid={`admin-filter-${t}`}
            >
              {t}{" "}
              <span className="opacity-65">
                ({t === "all" ? leads.length : leads.filter((l) => l.trade === t).length})
              </span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="surface p-10 text-center">
            <div className="font-display text-2xl mb-2">No leads yet.</div>
            <div className="text-sm text-[var(--af-cream)]/65">
              When a home-services pro fills out a form, they'll show up here.
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((lead) => (
              <div
                key={lead.id}
                className="surface p-5 grid lg:grid-cols-12 gap-4 items-start"
                data-testid={`admin-lead-row-${lead.id}`}
              >
                <div className="lg:col-span-3">
                  <div className="font-display text-xl">{lead.name}</div>
                  <div className="text-sm text-[var(--af-cream)]/75 flex items-center gap-1.5 mt-1">
                    <Building2 size={13} /> {lead.company}
                  </div>
                  <div className="mt-2 inline-block px-2.5 py-1 bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 text-[var(--af-lime)] text-[10px] uppercase tracking-wider rounded-full">
                    {lead.trade}
                  </div>
                </div>
                <div className="lg:col-span-3 text-sm">
                  <a
                    href={`mailto:${lead.email}`}
                    className="flex items-center gap-2 text-[var(--af-lime)] hover:underline underline-offset-4"
                  >
                    <Mail size={13} /> {lead.email}
                  </a>
                  {lead.phone && (
                    <a
                      href={`tel:${lead.phone}`}
                      className="flex items-center gap-2 text-[var(--af-cream)]/85 mt-1.5"
                    >
                      <Phone size={13} /> {lead.phone}
                    </a>
                  )}
                  {lead.source_page && (
                    <div className="flex items-center gap-2 text-xs text-[var(--af-cream)]/55 mt-1.5">
                      <MapPin size={11} /> {lead.source_page}
                    </div>
                  )}
                </div>
                <div className="lg:col-span-4">
                  <div className="text-[11px] uppercase tracking-widest text-[var(--af-cream)]/55 mb-1.5">
                    Package interest: {lead.package_interest || "—"}
                  </div>
                  {lead.message && (
                    <div className="text-sm text-[var(--af-cream)]/85 italic">
                      "{lead.message}"
                    </div>
                  )}
                </div>
                <div className="lg:col-span-2 flex flex-col items-end gap-2">
                  <div className="text-[10px] uppercase text-[var(--af-cream)]/55">
                    {new Date(lead.created_at).toLocaleString()}
                  </div>
                  <button
                    onClick={() => onDelete(lead.id)}
                    className="text-red-400 hover:text-red-300 p-1.5 border border-red-400/30 hover:border-red-300/50 rounded-md"
                    data-testid={`admin-delete-${lead.id}`}
                    aria-label="Delete lead"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminLeads;
